import { useCallback, useEffect, useState } from 'react';
import { api } from '../api/client.ts';
import { API_BASE, telechargerFichier } from '../api/fichiers.ts';
import SelecteurSemaine, { choixInitial } from '../components/SelecteurSemaine.tsx';
import BlocPartage from '../components/BlocPartage.tsx';
import ControleRshDhis2 from '../components/ControleRshDhis2.tsx';
import type { Choix } from '../components/SelecteurSemaine.tsx';
import { Aide, Message, Onglets, Pastille } from '../components/ui.tsx';
import { libelleSemaine } from '../../../shared/business-logic/semaines.ts';
import { AFFICHER_QR_TRANSFERT } from '../config/affichage.ts';

type Onglet = 'RSH' | 'MNT' | 'DHIS2';

const qs = (c: Choix, extra: Record<string, string> = {}) => new URLSearchParams({ ...(c.csbId ? { csbId: c.csbId } : {}), annee: String(c.annee), semaine: String(c.semaine), ...extra }).toString();

function Telechargements({ chemin, choix, formats, prefixe, extra }: { chemin: string; choix: Choix; formats: Array<[string, string]>; prefixe: string; extra?: Record<string, string> }) {
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  async function lancer(f: string) {
    setErreur(null); setEnCours(f);
    try { await telechargerFichier(`${API_BASE}${chemin}?${qs(choix, { format: f, ...extra })}`, `${prefixe}_S${choix.semaine}_${choix.annee}.${f}`); }
    catch (e: any) { setErreur(e.message ?? 'Téléchargement impossible.'); } finally { setEnCours(null); }
  }
  return (
    <div className="carte">
      <h2>Exports</h2>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {formats.map(([f, lib]) => <button key={f} className="secondaire" disabled={!!enCours} onClick={() => lancer(f)}>{enCours === f ? '…' : lib}</button>)}
      </div>
      {erreur && <Message niveau="bad">{erreur}</Message>}
    </div>
  );
}

/** Bandeau « S41 · Lundi 05/10/2026 → Dimanche 11/10/2026 » (semaine ISO de DHIS2). */
function SemaineLundiDimanche({ semaine }: { semaine: { semaine: number; annee: number; debut: string; fin: string } }) {
  const fr = (x: string) => `${x.slice(8, 10)}/${x.slice(5, 7)}/${x.slice(0, 4)}`;
  return (
    <div className="bandeau-dates" title={libelleSemaine({ ...semaine, jours: [], periodeDhis2: '' } as any)}>
      <span className="semaine">S{semaine.semaine}</span>
      <span className="plage">Lundi {fr(semaine.debut)} → Dimanche {fr(semaine.fin)}</span>
    </div>
  );
}

function Avertissements({ liste }: { liste: string[] }) {
  if (!liste.length) return <Message niveau="ok">Aucun avertissement.</Message>;
  return <Message niveau="warn"><b>{liste.length} point(s) à vérifier avant de partager :</b><ul style={{ margin: '6px 0 0 18px' }}>{liste.map((a, i) => <li key={i}>{a}</li>)}</ul></Message>;
}

// ---------------------------------------------------------------- RSH
function Rsh({ choix }: { choix: Choix }) {
  const [d, setD] = useState<any>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [zeros, setZeros] = useState(false);
  const [versionListe, setVersionListe] = useState('');   // vide = dernière liste publiée ; sinon version précise (reproduire un ancien rapport)
  const [saisie, setSaisie] = useState<Record<string, string>>({});
  const extraListe: Record<string, string> = versionListe ? { listeVersion: versionListe } : {};
  const charger = useCallback(async () => {
    setErreur(null);
    try { setD(await api.get(`/rapports/rsh?${qs(choix, { zerosConfirmes: zeros ? '1' : '0', ...(versionListe ? { listeVersion: versionListe } : {}) })}`)); } catch (e: any) { setD(null); setErreur(e.message); }
  }, [choix, zeros, versionListe]);
  useEffect(() => { if (choix.csbId) void charger(); }, [charger, choix.csbId]);
  if (!choix.csbId) return <p>Choisissez un établissement.</p>;
  if (erreur) return <Message niveau="bad">{erreur}</Message>;
  if (!d) return <p>Calcul en cours…</p>;
  async function saisirManuel(cle: string) {
    const v = Number(saisie[cle]);
    if (!Number.isInteger(v) || v < 0) { setErreur('Valeur : entier positif ou nul.'); return; }
    try { await api.put(`/rapports/rsh/manuel`, { csbId: choix.csbId, annee: choix.annee, semaine: choix.semaine, cle, valeur: v }); setSaisie((s) => ({ ...s, [cle]: '' })); await charger(); }
    catch (e: any) { setErreur(e.message); }
  }
  const r = d.rapport;
  return (
    <div>
      <div className="carte">
        <b>{d.csb.nom}</b>
        <SemaineLundiDimanche semaine={d.semaine} />
        <div className="jours-semaine" aria-label="Les 7 jours de la semaine, du lundi au dimanche">
          {r.jours.map((j: any) => (
            <div key={j.date} className={`jour-carte ${j.statut === 'SANS_DONNEE' ? 'manquant' : ''}`} title={j.statut === 'DONNEES' ? 'Données enregistrées' : 'Aucune saisie ce jour (manquant ≠ 0)'}>
              <span className="nom">{['', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'][j.jourSemaine]}</span>
              <span className="date">{j.date.slice(8)}/{j.date.slice(5, 7)}</span>
              <b>{j.statut === 'DONNEES' ? j.consultations : '–'}</b>
            </div>))}
        </div>
        <p className="note" style={{ margin: '4px 0 0' }}>{r.controle.lignesSemaine} consultation(s) datée(s){r.controle.sansDate ? ` · ${r.controle.sansDate} sans date (exclue${r.controle.sansDate > 1 ? 's' : ''})` : ''}{d.listeRsh?.numero ? ` · codes RSH v${d.listeRsh.numero}` : ''}
          <Aide>Méthode : somme des 7 journées (lundi → dimanche). Un jour sans saisie (–) est « manquant », pas « 0 ».{d.listeRsh?.publieLe ? ` Liste des codes publiée le ${String(d.listeRsh.publieLe).slice(0, 10)}.` : ''}</Aide></p>
      </div>
      <Avertissements liste={r.avertissements} />
      <div className="defil"><table className="saisie">
        <thead><tr><th>Code</th><th>Libellé</th><th>Valeur</th><th>Origine</th></tr></thead>
        <tbody>
          {r.lignes.map((l: any) => (
            <tr key={l.cle} className={l.origine === 'NON_RENSEIGNE' ? 'ligne-manquante' : undefined}>
              <td><b>{l.code}</b>{l.codePartage === null && <> <Pastille niveau="warn">non partagé</Pastille></>}</td>
              <td>{l.libelle}{l.aVerifier && <> <Pastille niveau="info">à vérifier</Pastille></>}{l.note && <div className="aide">{l.note}</div>}</td>
              <td style={{ textAlign: 'right' }}>
                {l.origine === 'NON_RENSEIGNE'
                  ? <span style={{ display: 'inline-flex', gap: 4 }}><input inputMode="numeric" style={{ width: 64 }} placeholder="saisir" value={saisie[l.cle] ?? ''} aria-label={`Valeur ${l.code}`} onChange={(e) => setSaisie({ ...saisie, [l.cle]: e.target.value.replace(/\D/g, '') })} /><button className="secondaire" onClick={() => saisirManuel(l.cle)} disabled={!(saisie[l.cle] ?? '')}>OK</button></span>
                  : <b>{l.valeur}</b>}
              </td>
              <td>{l.origine === 'CALCULE' ? 'Calculé' : l.origine === 'MANUEL' ? 'Saisi' : 'Non renseigné'}</td>
            </tr>
          ))}
        </tbody>
      </table></div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '12px 0' }}>
        <input type="checkbox" checked={zeros} onChange={(e) => setZeros(e.target.checked)} />
        Je confirme que les codes non renseignés valent 0 (« Les restes sont zéro cas. »)
      </label>
      <label htmlFor="rsh-version">Version de la liste des codes RSH (vide = dernière publiée)</label>
      <input id="rsh-version" inputMode="numeric" style={{ width: 90 }} value={versionListe} placeholder={d.listeRsh?.numero ? String(d.listeRsh.numero) : ''} onChange={(e) => setVersionListe(e.target.value.replace(/\D/g, '').slice(0, 4))} />
      {d.codesNonPartages.length > 0 && <Message niveau="info">Codes non inclus dans le texte (code ambigu dans le document officiel) : {[...new Set<string>(d.codesNonPartages)].join(', ')}. À confirmer avec le programme.</Message>}
      <BlocPartage texte={d.texte} titre={`RSH S${d.semaine.semaine}`} />
      {d.comparaisonDhis2 && <ControleRshDhis2 c={d.comparaisonDhis2} />}
      <Telechargements chemin="/rapports/rsh/export" choix={choix} prefixe="RSH" formats={[['xlsx', 'Excel (.xlsx)'], ['pdf', 'PDF'], ['txt', 'Texte (.txt)']]} extra={{ zerosConfirmes: zeros ? '1' : '0', ...extraListe }} />
    </div>
  );
}

// ---------------------------------------------------------------- MNT/MTN
function Mnt({ choix }: { choix: Choix }) {
  const [d, setD] = useState<any>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [zeros, setZeros] = useState(false);
  const [f, setF] = useState({ type: 'BILHARZIOSE_CONFIRME', date: '', valeur: '', dejaInclus: '' });
  const charger = useCallback(async () => {
    setErreur(null);
    try { setD(await api.get(`/rapports/mnt?${qs(choix, { zerosConfirmes: zeros ? '1' : '0' })}`)); } catch (e: any) { setD(null); setErreur(e.message); }
  }, [choix, zeros]);
  useEffect(() => { if (choix.csbId) void charger(); }, [charger, choix.csbId]);
  if (!choix.csbId) return <p>Choisissez un établissement.</p>;
  if (erreur && !d) return <Message niveau="bad">{erreur}</Message>;
  if (!d) return <p>Calcul en cours…</p>;
  const r = d.rapport;
  const suspect = f.type === 'BILHARZIOSE_SUSPECT';
  const dateMin = d.semaine.debut, dateMax = d.semaine.fin;
  async function ajouter() {
    setErreur(null);
    try {
      await api.post('/rapports/mnt/saisies', { csbId: choix.csbId, type: f.type, date: f.date, valeur: Number(f.valeur), ...(suspect ? { dejaInclusConsultations: f.dejaInclus === 'oui' } : {}) });
      setF({ ...f, valeur: '', dejaInclus: '' }); await charger();
    } catch (e: any) { setErreur([e.message, ...(e.details ?? [])].join(' — ')); }
  }
  async function supprimer(id: string) {
    if (!window.confirm('Supprimer cette saisie ?')) return;
    try { await api.delete(`/rapports/mnt/saisies/${id}`); await charger(); } catch (e: any) { setErreur(e.message); }
  }
  const lib: Record<string, string> = { BILHARZIOSE_SUSPECT: 'Cas suspect', BILHARZIOSE_CONFIRME: 'Cas confirmé', BILHARZIOSE_TRAITE: 'Cas traité' };
  const v = (x: number | null) => (x === null ? <i>non renseigné</i> : <b>{x}</b>);
  return (
    <div>
      <div className="carte"><b>{d.csb.nom}</b><SemaineLundiDimanche semaine={d.semaine} /></div>
      <Avertissements liste={r.avertissements} />
      <div className="defil"><table className="saisie">
        <thead><tr><th>Indicateur</th><th>Valeur</th><th>Détail</th></tr></thead>
        <tbody>
          <tr><td colSpan={3}><b>1. HTA</b></td></tr>
          <tr><td>Nombre Adulte 18ans et plus dépisté</td><td>{v(r.hta.depisteTotal)}</td><td className="aide">consultations {r.hta.depisteConsultations} + CPN {r.hta.depisteCpn} − doublons évités {r.hta.depisteDoublonsEvites}</td></tr>
          <tr><td>Nb total dépisté positif</td><td>{v(r.hta.positifsTotal)}</td><td className="aide">consultations {r.hta.positifsConsultations} + CPN {r.hta.positifsCpn} − doublons évités {r.hta.positifsDoublonsEvites}</td></tr>
          <tr><td colSpan={3}><b>2. DIABETE</b></td></tr>
          <tr><td>Nb Adultes 18ans et plus dépisté</td><td>{v(r.diabete.depisteCpn)}</td><td className="aide">dépistages du diabète en CPN</td></tr>
          <tr><td>Nb total testé positif</td><td>{v(r.diabete.positifsTotal)}</td><td className="aide">dépistés positifs CPN {r.diabete.positifsDepistageCpn} + diabète confirmé en consultation {r.diabete.confirmesConsultation} − doublons évités {r.diabete.positifsDoublonsEvites}</td></tr>
          <tr><td colSpan={3}><b>3. BILHARZIOSE</b></td></tr>
          <tr><td>Cas suspect</td><td>{v(r.bilharziose.suspectTotal)}</td><td className="aide">dysenterie en consultation {r.bilharziose.suspectConsultations ?? 'n/a'} + saisies {r.bilharziose.suspectManuel}</td></tr>
          <tr><td>Cas confirmé</td><td>{v(r.bilharziose.confirme)}</td><td className="aide">saisie manuelle</td></tr>
          <tr><td>Cas traité</td><td>{v(r.bilharziose.traite)}</td><td className="aide">saisie manuelle</td></tr>
        </tbody>
      </table></div>

      <div className="carte" style={{ marginTop: 12 }}>
        <h2>Saisie manuelle — bilharziose</h2>
        <label htmlFor="mnt-type">Type</label>
        <select id="mnt-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value, dejaInclus: '' })}>{Object.entries(lib).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <label htmlFor="mnt-date">Date</label>
        <input id="mnt-date" type="date" min={dateMin} max={dateMax} value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} />
        <label htmlFor="mnt-val">Nombre de cas (0 si aucun)</label>
        <input id="mnt-val" inputMode="numeric" value={f.valeur} onChange={(e) => setF({ ...f, valeur: e.target.value.replace(/\D/g, '') })} />
        {suspect && (<>
          <label htmlFor="mnt-inclus">Ces cas suspects sont-ils déjà enregistrés dans les consultations (dysenterie) ?</label>
          <select id="mnt-inclus" value={f.dejaInclus} onChange={(e) => setF({ ...f, dejaInclus: e.target.value })}>
            <option value="">— répondre —</option><option value="oui">Oui, déjà inclus (ne pas les rajouter)</option><option value="non">Non, à ajouter</option>
          </select></>)}
        <button onClick={ajouter} disabled={!f.date || f.valeur === '' || (suspect && !f.dejaInclus)} style={{ marginTop: 8 }}>Ajouter</button>
        {erreur && <Message niveau="bad">{erreur}</Message>}
        {d.saisies.length > 0 && (
          <ul style={{ marginTop: 10 }}>{d.saisies.map((s: any) => (
            <li key={s.id}>{lib[s.type]} — {s.date} : <b>{s.valeur}</b>{s.type === 'BILHARZIOSE_SUSPECT' ? (s.dejaInclusConsultations ? ' (déjà inclus)' : ' (ajouté)') : ''} <button className="secondaire" onClick={() => supprimer(s.id)}>Supprimer</button></li>))}</ul>)}
      </div>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '12px 0' }}>
        <input type="checkbox" checked={zeros} onChange={(e) => setZeros(e.target.checked)} />
        Je confirme que les valeurs non renseignées valent 0 dans le texte partagé
      </label>
      <BlocPartage texte={d.texte} titre={`MNT/MTN S${d.semaine.semaine}`} />
      <Telechargements chemin="/rapports/mnt/export" choix={choix} prefixe="MNT_MTN" formats={[['xlsx', 'Excel (.xlsx)'], ['pdf', 'PDF'], ['txt', 'Texte (.txt)']]} extra={{ zerosConfirmes: zeros ? '1' : '0' }} />
    </div>
  );
}

// ---------------------------------------------------------------- DHIS2
const COURT: Record<string, string> = { SAISI: 'saisi', ZERO_DEFAUT: 'zéro défaut', NON_CLOS: 'non clos', FERME: 'fermé', NON_EXPORTE: 'non exporté' };
const NIVEAU: Record<string, 'ok' | 'info' | 'warn' | 'bad'> = { SAISI: 'ok', ZERO_DEFAUT: 'info', NON_CLOS: 'warn', FERME: 'warn', NON_EXPORTE: 'warn' };
const JOURS = ['', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

/** Liste des jours de la semaine avec leur statut, avant l'export, et heure d'export utilisée (heure de Madagascar, calculée par le serveur). */
function StatutsJoursDhis2({ jours, zero }: { jours: any[]; zero: any }) {
  const fr = (x: string) => `${x.slice(8, 10)}/${x.slice(5, 7)}`;
  const jourSem = (x: string) => JOURS[((new Date(`${x}T00:00:00Z`).getUTCDay() + 6) % 7) + 1];
  return (
    <div className="carte">
      <h2>Statut des jours avant export</h2>
      <p className="note" style={{ margin: '0 0 6px' }}>Heure d'export utilisée (heure de Madagascar, fournie par le serveur) : <b>{zero.instantExport?.libelle ?? '—'}</b>
        {zero.reglages.actif ? <> · jour clos à partir de <b>{zero.reglages.heureLimite}</b>{zero.reglages.jourRepos ? <> (jour de repos : clos dès 00h00)</> : null}</> : <> · « zéro par défaut » <b>non activé</b> pour ce CSB : aucun zéro n'est généré (Référentiels → Établissements).</>}</p>
      <ul style={{ margin: 0, paddingLeft: 18 }}>
        {jours.map((j) => (
          <li key={j.date}><b>{jourSem(j.date)} {fr(j.date)}</b> — <Pastille niveau={NIVEAU[j.statut] ?? 'info'}>{j.libelleStatut}</Pastille>{j.statut === 'SAISI' ? ` (${j.consultations} consultation(s))` : ''}
            {j.statut === 'SAISI' && !j.completeDateInclus ? <span className="aide"> — Nombre total de patient de la journée à saisir dans DHIS2 pour marquer le rapport complet</span> : null}</li>))}
      </ul>
    </div>
  );
}

/** Exports DHIS2 : Excel / PDF, et DEUX fichiers distincts (saisis = Merge ; zéros = Append) + mode d'emploi. */
function ExportsDhis2({ choix, d, joursVides }: { choix: Choix; d: any; joursVides: boolean }) {
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState<string | null>(null);
  const z = d.zeroParDefaut;
  async function lancer(cle: string, f: string, fichier?: 'saisis' | 'zeros') {
    setErreur(null); setEnCours(cle);
    const nom = fichier ? z.fichiers[f][fichier] : `DHIS2_S${choix.semaine}_${choix.annee}.${f}`;
    try { await telechargerFichier(`${API_BASE}/rapports/dhis2/export?${qs(choix, { format: f, joursVides: joursVides ? '1' : '0', ...(fichier ? { fichier } : {}) })}`, nom); }
    catch (e: any) { setErreur(e.message ?? 'Téléchargement impossible.'); } finally { setEnCours(null); }
  }
  const btn = (cle: string, lib: string, f: string, fichier?: 'saisis' | 'zeros', off = false) => <button key={cle} className="secondaire" disabled={!!enCours || off} onClick={() => lancer(cle, f, fichier)}>{enCours === cle ? '…' : lib}</button>;
  return (
    <div className="carte">
      <h2>Exports</h2>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {btn('xlsx', 'Excel — tableau de saisie', 'xlsx')}
        {btn('pdf', 'PDF (avec QR)', 'pdf')}
      </div>
      {!d.identifiantManquant && (<>
        <h3 style={{ marginBottom: 4 }}>Fichiers d'import DHIS2 (deux fichiers distincts)</h3>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {btn('json-s', `JSON saisis — Merge (${z.nbJoursSaisis} jour(s))`, 'json', 'saisis', z.nbJoursSaisis === 0)}
          {btn('csv-s', `CSV saisis — Merge`, 'csv', 'saisis', z.nbJoursSaisis === 0)}
          {z.nbJoursZero > 0 && btn('json-z', `JSON zéros — Append (${z.nbJoursZero} jour(s))`, 'json', 'zeros')}
          {z.nbJoursZero > 0 && btn('csv-z', `CSV zéros — Append`, 'csv', 'zeros')}
        </div>
        <Message niveau="info"><b>Mode d'emploi DHIS2</b><ul style={{ margin: '6px 0 0 18px' }}>{z.modeEmploi.map((l: string, i: number) => <li key={i}>{l}</li>)}</ul></Message>
        {z.nbJoursZero === 0 && <p className="aide">Aucun jour à zéro par défaut : le second fichier n'est pas généré.</p>}
      </>)}
      {erreur && <Message niveau="bad">{erreur}</Message>}
    </div>
  );
}

function Dhis2({ choix }: { choix: Choix }) {
  const [d, setD] = useState<any>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [joursVides, setJoursVides] = useState(false);
  useEffect(() => {
    if (!choix.csbId) return;
    setErreur(null);
    api.get(`/rapports/dhis2?${qs(choix, { joursVides: joursVides ? '1' : '0' })}`).then(setD).catch((e: any) => { setD(null); setErreur(e.message); });
  }, [choix, joursVides]);
  if (!choix.csbId) return <p>Choisissez un établissement.</p>;
  if (erreur) return <Message niveau="bad">{erreur}</Message>;
  if (!d) return <p>Calcul en cours…</p>;
  const jours: any[] = d.recap.jours;
  return (
    <div>
      <div className="carte"><b>{d.csb.nom}</b><SemaineLundiDimanche semaine={d.semaine} /><Aide>Jeu de données DHIS2 « {d.dhis2.jeuDeDonneesNom} » — période journalière (DHIS2 {d.dhis2.version}) : le récapitulatif se saisit jour par jour.</Aide></div>
      {AFFICHER_QR_TRANSFERT && <Message niveau="warn"><b>Importation par QR code :</b> {d.dhis2.qrImportOfficiel.raison} {d.dhis2.qrImportOfficiel.repli}</Message>}
      {d.identifiantManquant && <Message niveau="warn"><b>Identifiant DHIS2 manquant pour cet établissement.</b> Les exports JSON, CSV et le QR sont désactivés ; le tableau de saisie, l'Excel et le contrôle restent disponibles. Un administrateur doit le saisir dans Référentiels → Établissements.</Message>}
      <Avertissements liste={[...d.erreursValidation, ...d.recap.avertissements.filter((a: string) => !a.startsWith('Identifiant DHIS2 de l'))]} />
      <StatutsJoursDhis2 jours={jours} zero={d.zeroParDefaut} />
      {d.comparaison && <ControleRshDhis2 c={d.comparaison} />}
      <div className="defil"><table className="saisie">
        <thead><tr><th>Élément DHIS2</th>{jours.map((j) => <th key={j.date}>{j.date.slice(8)}/{j.date.slice(5, 7)}</th>)}<th>Total</th></tr></thead>
        <tbody>
          <tr className="aide"><td><i>Statut du jour</i></td>{jours.map((j) => <td key={j.date} style={{ textAlign: 'right', fontSize: 11 }} title={j.libelleStatut}>{COURT[j.statut] ?? ''}</td>)}<td /></tr>
          {d.recap.totaux.map((t: any) => (
          <tr key={t.elementId + t.categoryOptionCombo}>
            <td>{t.elementNom}{!t.confirme && <> <Pastille niveau="info">à vérifier</Pastille></>}</td>
            {jours.map((j) => { const x = j.dataValueSet?.dataValues.find((v: any) => v.dataElement === t.elementId && v.categoryOptionCombo === t.categoryOptionCombo); return <td key={j.date} style={{ textAlign: 'right' }} title={j.statut === 'ZERO_DEFAUT' ? 'zéro par défaut (aucune saisie)' : undefined}>{x ? x.value : ''}{x && j.statut === 'ZERO_DEFAUT' ? <sup> z</sup> : null}</td>; })}
            <td style={{ textAlign: 'right' }}><b>{t.total}</b></td>
          </tr>))}</tbody>
      </table></div>
      <p className="aide"><sup>z</sup> = « zéro par défaut (aucune saisie) » : valeur présente uniquement dans le fichier d'export, jamais enregistrée dans PRE RMA ni ajoutée aux totaux.</p>
      <label style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '12px 0' }}>
        <input type="checkbox" checked={joursVides} onChange={(e) => setJoursVides(e.target.checked)} />
        Forcer aussi les jours sans consultation en « zéro par défaut » (même non clos, hors jours fermés) — à n'utiliser que si le CSB était ouvert ces jours-là
      </label>
      <ExportsDhis2 choix={choix} d={d} joursVides={joursVides} />
      {AFFICHER_QR_TRANSFERT && d.qrTransfert.length > 0 && (
        <div className="carte">
          <h2>QR de transfert PRE RMA</h2>
          <Message niveau="info">Ces QR transportent uniquement des valeurs agrégées (aucune donnée de patient). Ils ne sont <b>pas</b> importables directement par DHIS2.</Message>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
            {d.qrTransfert.map((q: any) => (
              <figure key={`${q.date}-${q.index}`} style={{ margin: 0, textAlign: 'center' }}>
                {/* SVG produit par notre propre encodeur côté serveur (contenu maîtrisé : rectangles uniquement) */}
                <div dangerouslySetInnerHTML={{ __html: q.svg }} />
                <figcaption className="aide">{q.date} — {q.index}/{q.total}</figcaption>
              </figure>))}
          </div>
        </div>)}
    </div>
  );
}

export default function RapportsHebdo() {
  const [choix, setChoix] = useState<Choix>(choixInitial);
  const [onglet, setOnglet] = useState<Onglet>('RSH');
  return (
    <div>
      <h1>Rapports journaliers et hebdomadaires</h1>
      <SelecteurSemaine choix={choix} onChange={setChoix} />
      <Onglets<Onglet> actif={onglet} onChoix={setOnglet} onglets={[{ id: 'RSH', label: 'RSH' }, { id: 'DHIS2', label: 'DHIS2' }, { id: 'MNT', label: 'MNT/MTN' }]} />
      {onglet === 'RSH' && <Rsh choix={choix} />}
      {onglet === 'MNT' && <Mnt choix={choix} />}
      {onglet === 'DHIS2' && <Dhis2 choix={choix} />}
    </div>
  );
}
